import { Injectable, inject, computed } from '@angular/core';
import {
  OrderRexAriaInfo,
  OrderRexAriaPayload,
} from '../models/order-rex-aria.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import { firstValueFrom } from 'rxjs';
import { injectQuery, queryOptions } from '@ngneat/query';
import { derivedAsync } from 'ngxtension/derived-async';
import { TenantUrlUtilService } from '@shared/services/tenant-url-util.service';

@Injectable({
  providedIn: 'root',
})
export class OrderRexAriaService {
  private readonly http = inject(HttpClient);
  private readonly query = injectQuery();
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);

  public async fetchNotifications(
    payload: OrderRexAriaPayload
  ): Promise<OrderRexAriaInfo[]> {
    try {
      if (
        !payload['date-from'] ||
        !payload['date-to'] ||
        !payload.searchType ||
        !payload.searchValue ||
        !payload.tenantId
      ) {
        return [];
      }

      const params = new HttpParams()
        .set('date-from', payload['date-from'])
        .set('date-to', payload['date-to'])
        .set('searchType', payload.searchType)
        .set('searchValue', payload.searchValue);

      const response = await firstValueFrom(
        this.http.get<OrderRexAriaInfo[]>(
          `${environment.API_SERVICES}/api/customer-care/v2/${payload.tenantId}/notifications`,
          { params }
        )
      );
      return response || [];
    } catch (error) {
      return [];
    }
  }

  private createNotificationsMap(
    notifications: OrderRexAriaInfo[]
  ): Map<string, OrderRexAriaInfo> {
    return new Map(notifications.map((d) => [d.orderId, d]));
  }

  private getNotificationsQueryOptions(payload: OrderRexAriaPayload) {
    return queryOptions({
      queryKey: ['orders', 'rex-aria-notifications', payload] as const,
      queryFn: async () => await this.fetchNotifications(payload),
    });
  }

  readonly notificationsQuery = derivedAsync(() => {
    const tenantInfo = this.tenantUrlUtil.tenantUrlInfo();
    const dateRange = this.tenantUrlUtil.getDateRangeForAPI()

    const payload: OrderRexAriaPayload = {
      'date-from': dateRange?.dateFrom,
      'date-to': dateRange?.dateTo,
      searchType: tenantInfo.documentType as OrderRexAriaPayload['searchType'],
      searchValue: tenantInfo?.documentValue!,
      tenantId: tenantInfo?.tenant!,
    };

    return this.query({
      ...this.getNotificationsQueryOptions(payload),
      enabled: !!tenantInfo.tenant && !!tenantInfo.documentValue && !!dateRange,
    }).result$;
  });

  readonly loaded = computed(() => {
    const query = this.notificationsQuery();
    return !query?.isLoading && !!query?.data;
  });

  readonly error = computed(() => {
    const query = this.notificationsQuery();
    return query?.error ?? null;
  });

  readonly dataRexAria = computed(() => {
    const query = this.notificationsQuery();
    return this.createNotificationsMap(query?.data ?? []);
  });

  readonly isLoading = computed(() => {
    const query = this.notificationsQuery();
    return query?.isLoading ?? false;
  });
}
